import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { typography, palette } from '../../../../utils/designTokens';

/**
 * Accordion - Reusable accordion component with sticky headers
 *
 * Usage:
 * <Accordion
 *   sections={[
 *     { title: "Section 1", content: <div>Content 1</div>, defaultExpanded: false },
 *     { title: "Section 2", content: <div>Content 2</div>, defaultExpanded: true }
 *   ]}
 * />
 *
 * @param {Array} sections - Array of section objects with:
 *   - title: string - Section title
 *   - content: React.ReactNode - Section content
 *   - defaultExpanded: boolean - Initial expanded state (optional, default: false)
 *   - headerActions: React.ReactNode - Optional actions rendered on the right side of the header
 * @param {boolean} nested - If true, indents headers and content to show hierarchy
 * @param {boolean} scrollable - If true (default), the accordion owns its own scroll
 *   region and headers stick to its top edge as you scroll within it. Set false when
 *   the accordion instead sits inside content that already scrolls as a whole (e.g. a
 *   drawer body) — nesting two independent scroll regions is the "double scrollbar"
 *   problem. When false, headers render statically (no sticky stacking).
 */
const Accordion = ({ sections = [], nested = false, scrollable = true }) => {
  // Create a stable key based on section titles to detect when sections change
  const sectionsKey = useMemo(() =>
    sections.map(s => s.title).join('|'),
    [sections]
  );

  // Initialize state based on defaultExpanded values
  const [expandedStates, setExpandedStates] = useState(
    sections.reduce((acc, section, index) => {
      acc[index] = section.defaultExpanded || false;
      return acc;
    }, {})
  );

  // Reset expanded states when sections change (e.g., switching between spans)
  useEffect(() => {
    setExpandedStates(
      sections.reduce((acc, section, index) => {
        acc[index] = section.defaultExpanded || false;
        return acc;
      }, {})
    );
  }, [sectionsKey]);

  const toggleSection = (index) => {
    const isExpanding = !expandedStates[index];
    setExpandedStates(prev => ({ ...prev, [index]: !prev[index] }));
    if (isExpanding && sections[index].onExpand) {
      sections[index].onExpand();
    }
  };

  // Calculate how many sections are before this one for sticky positioning
  const calculateStickyTop = (index) => {
    return `${index * 44}px`;
  };

  // Z-index decreases as we go down (highest at top)
  const calculateZIndex = (index) => {
    return 40 - (index * 10);
  };

  return (
    <div className={`flex flex-col ${scrollable ? 'h-full overflow-auto' : ''}`}>
      {sections.map((section, index) => {
        const expanded = expandedStates[index];

        return (
          <React.Fragment key={index}>
            <div
              className="w-full flex items-center justify-between p-2 rounded transition-colors hover:bg-opacity-5 border-t"
              style={{
                backgroundColor: palette.neutrals.panelSecondary,
                color: palette.text.primary,
                borderColor: palette.borders.subtle,
                position: scrollable ? 'sticky' : 'static',
                top: scrollable ? calculateStickyTop(index) : undefined,
                minHeight: 44,
                maxHeight: 44,
                height: 44,
                boxSizing: 'border-box',
                zIndex: scrollable ? calculateZIndex(index) : undefined,
                marginTop: 0,
                marginBottom: 0,
                paddingLeft: nested ? 20 : undefined,
              }}
            >
              <button
                onClick={() => toggleSection(index)}
                className="flex items-center flex-1"
              >
                {expanded ? (
                  <ChevronDown size={16} style={{ color: nested ? palette.text.muted : palette.text.supporting }} />
                ) : (
                  <ChevronRight size={16} style={{ color: nested ? palette.text.muted : palette.text.supporting }} />
                )}
                <span
                  className={`${typography.size.metadata} font-semibold uppercase`}
                  style={{ color: nested ? palette.text.muted : palette.text.supporting }}
                >
                  {section.title}
                </span>
              </button>
              {section.headerActions && (
                <div onClick={(e) => e.stopPropagation()}>
                  {section.headerActions}
                </div>
              )}
            </div>
            {expanded && (
              <div className="mt-2 mb-0" style={nested ? { paddingLeft: 20 } : undefined}>
                {section.content}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Accordion;
